import {
  useGetDeployment,
  useGetRole,
  useGetTenant,
  useGetUser,
  useGetWorkspace,
} from '@sunstone/api-client/hooks';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@sunstone/ui';
import { Box, Building, Building2, Layers, Server } from 'lucide-react';
import type React from 'react';
import { Fragment } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuthorization, useAuthToken } from '@/features/auth';

interface Crumb {
  label: string;
  to?: string;
  icon?: React.ComponentType<{ className?: string }>;
}

function useBreadcrumbs(): Crumb[] {
  const location = useLocation();
  const { token } = useAuthToken();
  const { isPlatformAdmin, scope, tenantId: userOwnTenantId } = useAuthorization();
  const segments = location.pathname.split('/').filter(Boolean);

  // Parse IDs from the URL so we can pre-fetch labels
  const isTenantRoute = segments[0] === 'tenants';
  const tenantId = isTenantRoute && segments[1] && segments[1] !== 'new' ? segments[1] : '';
  const deploymentId =
    tenantId && segments[2] === 'deployments' && segments[3] && segments[3] !== 'new'
      ? segments[3]
      : '';
  const workspaceId =
    deploymentId && segments[4] === 'workspaces' && segments[5] && segments[5] !== 'new'
      ? segments[5]
      : '';
  const roleId = segments[0] === 'roles' && segments[1] && segments[1] !== 'new' ? segments[1] : '';
  const newRoleTenantId =
    segments[0] === 'roles' && segments[1] === 'new'
      ? (new URLSearchParams(location.search).get('tenantId') ?? '')
      : '';
  const platformUserId =
    segments[0] === 'platform-users' && segments[1] === 'users' && segments[2] ? segments[2] : '';
  const tenantUserId = tenantId && segments[2] === 'users' && segments[3] ? segments[3] : '';

  const { data: tenantData } = useGetTenant(tenantId, token);
  const { data: deploymentData } = useGetDeployment(deploymentId, token);
  const { data: workspaceData } = useGetWorkspace(workspaceId, token);
  const { data: roleData } = useGetRole(roleId, token);
  const { data: roleTenantData } = useGetTenant(roleData?.role?.tenantId ?? '', token);
  const { data: newRoleTenantData } = useGetTenant(newRoleTenantId, token);
  const { data: platformUserData } = useGetUser(platformUserId, token);
  const { data: tenantUserData } = useGetUser(tenantUserId, token);
  // For customer tenants, fetch the parent (partner) to use as the root breadcrumb label.
  // Only needed for all-tenant-scope and partner-level identities — others don't show the parent crumb.
  const canSeeParentCrumb = scope !== 'tenant';
  const parentId = canSeeParentCrumb ? (tenantData?.tenant?.parentId ?? '') : '';
  const { data: partnerData } = useGetTenant(parentId, token);

  if (segments.length === 0) return [];

  // Non-platform-admin on their own tenant home page (/tenants/:theirId) — no breadcrumb
  if (scope !== 'all' && isTenantRoute && segments.length === 2 && tenantId === userOwnTenantId) {
    return [];
  }

  const crumbs: Crumb[] = [];

  // /partners/new
  if (segments[0] === 'partners' && segments[1] === 'new') {
    crumbs.push({ label: 'Partners', to: '/partners' });
    crumbs.push({ label: 'New Partner' });
    return crumbs;
  }

  // /customers/new
  if (segments[0] === 'customers' && segments[1] === 'new') {
    crumbs.push({ label: 'Customers', to: '/customers' });
    crumbs.push({ label: 'New Customer' });
    return crumbs;
  }

  // /tenants/…
  if (segments[0] === 'tenants') {
    const partner = partnerData?.tenant;
    const isPartner = tenantData?.tenant ? !tenantData.tenant.parentId : false;

    // Root crumb: only add if the user can navigate to it.
    // Child tenant: show parent partner crumb for all-tenant-scope and partner-level identities only.
    // Partner tenant: show "Partners" root only for a tenant-less (platform) Admin.
    if (partner) {
      if (scope !== 'tenant') {
        crumbs.push({ label: partner.displayName, to: `/tenants/${partner.id}`, icon: Building2 });
      }
    } else if (isPartner) {
      if (isPlatformAdmin) {
        crumbs.push({ label: 'Partners', to: '/partners', icon: Building2 });
      }
    } else {
      crumbs.push({ label: 'Customers', to: '/customers' });
    }

    // /tenants/:tenantId
    if (segments[1]) {
      const tenantLabel = tenantData?.tenant?.displayName ?? segments[1];
      const isLast = segments.length === 2;
      crumbs.push({
        label: tenantLabel,
        to: isLast ? undefined : `/tenants/${segments[1]}`,
        icon: isPartner ? Building2 : Building,
      });

      // /tenants/:tenantId/edit
      if (segments[2] === 'edit') {
        crumbs.push({ label: 'Edit' });
        return crumbs;
      }

      // /tenants/:tenantId/invite
      if (segments[2] === 'invite') {
        crumbs.push({ label: 'Invite Member' });
        return crumbs;
      }

      // /tenants/:tenantId/users/:userId and /tenants/:tenantId/users/:userId/edit
      if (segments[2] === 'users' && segments[3]) {
        const userLabel = tenantUserData?.user?.name || tenantUserData?.user?.email || segments[3];
        crumbs.push({
          label: userLabel,
          to: segments[4] === 'edit' ? `/tenants/${segments[1]}/users/${segments[3]}` : undefined,
        });
        if (segments[4] === 'edit') crumbs.push({ label: 'Edit' });
        return crumbs;
      }

      // /tenants/:tenantId/deployments/new
      if (segments[2] === 'deployments' && segments[3] === 'new') {
        crumbs.push({ label: 'New Deployment' });
        return crumbs;
      }

      // /tenants/:tenantId/deployments/:deploymentId
      if (segments[2] === 'deployments' && segments[3]) {
        const deploymentLabel = deploymentData?.deployment?.displayName ?? segments[3];
        const depIsLast = segments.length === 4;
        crumbs.push({
          label: deploymentLabel,
          to: depIsLast ? undefined : `/tenants/${segments[1]}/deployments/${segments[3]}`,
          icon: Server,
        });

        // /tenants/:tenantId/deployments/:deploymentId/workspaces/new
        if (segments[4] === 'workspaces' && segments[5] === 'new') {
          crumbs.push({ label: 'New Workspace' });
          return crumbs;
        }

        // /tenants/:tenantId/deployments/:deploymentId/workspaces/:workspaceId
        if (segments[4] === 'workspaces' && segments[5]) {
          const wsLabel = workspaceData?.workspace?.displayName ?? segments[5];
          const hasComponent = segments[6] === 'components' && !!segments[7];
          crumbs.push({
            label: wsLabel,
            to: hasComponent
              ? `/tenants/${segments[1]}/deployments/${segments[3]}/workspaces/${segments[5]}`
              : undefined,
            icon: Layers,
          });
          if (hasComponent) {
            const raw = segments[7] as string;
            const formatted = raw.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
            crumbs.push({ label: formatted, icon: Box });
          }
        }
      }
    }

    return crumbs;
  }

  // /roles/new
  if (segments[0] === 'roles' && segments[1] === 'new') {
    if (newRoleTenantId) {
      crumbs.push({
        label: newRoleTenantData?.tenant?.displayName ?? newRoleTenantId,
        to: `/tenants/${newRoleTenantId}`,
        icon: Building2,
      });
    } else {
      crumbs.push({ label: 'Roles', to: '/system-roles' });
    }
    crumbs.push({ label: 'New Role' });
    return crumbs;
  }

  // /roles/:roleId and /roles/:roleId/edit — a tenant-scoped role's parent
  // is its own tenant; a system/platform-scope role's parent is Roles.
  if (segments[0] === 'roles' && segments[1]) {
    const role = roleData?.role;
    if (role?.tenantId) {
      crumbs.push({
        label: roleTenantData?.tenant?.displayName ?? role.tenantId,
        to: `/tenants/${role.tenantId}`,
        icon: Building2,
      });
    } else {
      crumbs.push({ label: 'Roles', to: '/system-roles' });
    }
    crumbs.push({
      label: role?.name ?? segments[1],
      to: segments[2] === 'edit' ? `/roles/${segments[1]}` : undefined,
    });
    if (segments[2] === 'edit') crumbs.push({ label: 'Edit' });
    return crumbs;
  }

  // /platform-users/invite
  if (segments[0] === 'platform-users' && segments[1] === 'invite') {
    crumbs.push({ label: 'Platform Users', to: '/platform-users' });
    crumbs.push({ label: 'Invite Platform User' });
    return crumbs;
  }

  // /platform-users/users/:userId and /platform-users/users/:userId/edit
  if (segments[0] === 'platform-users' && segments[1] === 'users' && segments[2]) {
    const userLabel = platformUserData?.user?.name || platformUserData?.user?.email || segments[2];
    crumbs.push({ label: 'Platform Users', to: '/platform-users' });
    crumbs.push({
      label: userLabel,
      to: segments[3] === 'edit' ? `/platform-users/users/${segments[2]}` : undefined,
    });
    if (segments[3] === 'edit') crumbs.push({ label: 'Edit' });
    return crumbs;
  }

  // Simple single-segment pages — capitalise the segment
  const PAGE_LABELS: Record<string, string> = {
    dashboard: 'Dashboard',
    infrastructure: 'Infrastructure',
    deployments: 'Deployments',
    workspaces: 'Workspaces',
    releases: 'Releases',
    status: 'Status',
    settings: 'Settings',
    preferences: 'Preferences',
    chat: 'AI Assistant',
    'system-roles': 'Roles',
    'platform-users': 'Platform Users',
  };

  const label = segments[0] ? PAGE_LABELS[segments[0]] : undefined;
  if (label) crumbs.push({ label });

  return crumbs;
}

export function Breadcrumbs() {
  const crumbs = useBreadcrumbs();

  // Don't render on root or single-crumb pages with no ancestor
  if (crumbs.length < 2) return null;

  return (
    <div className="border-b border-border/60 bg-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center">
        <Breadcrumb>
          <BreadcrumbList>
            {crumbs.map((crumb, i) => {
              const isLast = i === crumbs.length - 1;
              const Icon = crumb.icon;
              return (
                <Fragment key={crumb.to ?? crumb.label}>
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage className="flex items-center gap-1">
                        {Icon && <Icon className="h-3.5 w-3.5" />}
                        {crumb.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={crumb.to ?? '#'} className="flex items-center gap-1">
                          {Icon && <Icon className="h-3.5 w-3.5" />}
                          {crumb.label}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                  {!isLast && <BreadcrumbSeparator />}
                </Fragment>
              );
            })}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
    </div>
  );
}
